import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Zap, TrendingUp, TrendingDown, Minus, Plus, ArrowLeft, Package, ShieldCheck,
} from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import PriceTrendChart from "@/components/PriceTrendChart";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        setQty(data.moq || 1);
      } catch {
        toast.error("Product not found");
        nav("/catalog");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-zinc-100 p-10">
        <div className="h-96 bg-zinc-200 animate-pulse" />
      </div>
    );
  }

  const trend = product.price_trend || [];
  const priceStart = trend[0]?.price || product.price;
  const priceEnd = trend[trend.length - 1]?.price || product.price;
  const change = ((priceEnd - priceStart) / priceStart) * 100;
  const isUp = change >= 0;

  const total = product.price * qty;
  const canOrder = qty >= (product.moq || 1) && qty <= product.stock;

  const requireAuth = () => {
    if (!user) {
      toast.error("Sign in to continue");
      nav("/login");
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await addToCart(product.id, qty);
      toast.success(`Added ${qty} ${product.unit} to cart`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const handleBuyNow = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await addToCart(product.id, qty);
      nav("/checkout?buynow=1");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="px-6 lg:px-10 py-6">
        <Link
          to="/catalog"
          data-testid="back-to-catalog"
          className="inline-flex items-center gap-2 font-mono-data text-xs tracking-widest hover:text-[#FF5A00]"
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO CATALOG
        </Link>
      </div>

      <div className="px-6 lg:px-10 pb-16 grid lg:grid-cols-5 gap-8">
        {/* Left: image + details */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border-2 border-zinc-900 aspect-[4/3] overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="bg-white border border-zinc-900 p-6">
            <div className="tag">SKU · {product.sku}</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">
              {product.name}
            </h1>
            <p className="text-zinc-600 mt-3 leading-relaxed">{product.description}</p>

            <div className="mt-6">
              <div className="tag mb-3">TECHNICAL SPECIFICATIONS</div>
              <table className="w-full border border-zinc-300">
                <tbody className="font-mono-data text-sm">
                  {Object.entries(product.specs || {}).map(([k, v]) => (
                    <tr key={k} className="border-b border-zinc-200 last:border-0">
                      <td className="p-3 bg-zinc-50 text-zinc-500 tag" style={{ letterSpacing: "0.15em" }}>
                        {k}
                      </td>
                      <td className="p-3 font-medium">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, label: "Purity assured" },
                { icon: Package, label: "MOQ " + product.moq + " " + product.unit },
                { icon: TrendingUp, label: "Live pricing" },
              ].map((b, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 border border-zinc-900 px-3 py-2 font-mono-data text-xs"
                >
                  <b.icon className="w-4 h-4 text-[#FF5A00]" />
                  {b.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Actions + Trend */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border-2 border-zinc-900 p-6 sticky top-24">
            <div className="flex items-baseline justify-between border-b border-zinc-200 pb-4">
              <div>
                <div className="tag">CURRENT PRICE</div>
                <div className="font-mono-data text-4xl font-bold mt-1">
                  ₹{product.price.toFixed(2)}
                </div>
                <div className="tag mt-1">per {product.unit} · ex GST</div>
              </div>
              <div
                className={`flex items-center gap-1 font-mono-data font-bold text-sm ${
                  isUp ? "text-green-700" : "text-red-600"
                }`}
              >
                {isUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {change.toFixed(2)}%
              </div>
            </div>

            <div className="py-4 border-b border-zinc-200">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-zinc-500">Stock available</span>
                <span
                  data-testid="product-stock"
                  className="font-mono-data font-bold text-green-700"
                >
                  {product.stock.toLocaleString()} {product.unit}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Minimum order (MOQ)</span>
                <span className="font-mono-data font-bold">
                  {product.moq} {product.unit}
                </span>
              </div>
            </div>

            <div className="py-5">
              <div className="tag mb-2">SELECT QUANTITY ({product.unit})</div>
              <div className="flex items-stretch border-2 border-zinc-900">
                <button
                  data-testid="qty-decrement"
                  onClick={() => setQty(Math.max(product.moq, qty - product.moq))}
                  className="px-4 hover:bg-zinc-900 hover:text-white transition"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  data-testid="qty-input"
                  type="number"
                  value={qty}
                  onChange={(e) =>
                    setQty(Math.max(1, Math.min(product.stock, Number(e.target.value) || 1)))
                  }
                  className="flex-1 text-center font-mono-data text-lg font-bold outline-none border-x-2 border-zinc-900"
                />
                <button
                  data-testid="qty-increment"
                  onClick={() => setQty(Math.min(product.stock, qty + product.moq))}
                  className="px-4 hover:bg-zinc-900 hover:text-white transition"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {qty < product.moq && (
                <p className="text-xs text-red-600 mt-2 font-mono-data">
                  Minimum {product.moq} {product.unit} required
                </p>
              )}
            </div>

            <div className="py-4 border-t border-b border-zinc-200 flex justify-between items-baseline">
              <span className="tag">LINE TOTAL</span>
              <span
                className="font-mono-data text-2xl font-bold"
                data-testid="line-total"
              >
                ₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </div>

            <div className="pt-5 space-y-3">
              <button
                onClick={handleBuyNow}
                disabled={!canOrder || busy}
                data-testid="buy-now-button"
                className="w-full bg-[#FF5A00] text-white py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" /> BUY NOW
              </button>
              <button
                onClick={handleAdd}
                disabled={!canOrder || busy}
                data-testid="add-to-cart-button"
                className="w-full bg-white text-zinc-900 py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-4 h-4" /> ADD TO CART
              </button>
            </div>
          </div>

          <div className="bg-white border border-zinc-900 p-6">
            <div className="tag mb-2">30-DAY PRICE TREND</div>
            <PriceTrendChart data={trend} height={200} />
          </div>
        </div>
      </div>
    </div>
  );
}
